import { ref, computed } from 'vue'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '@/firebase'
import { devLog } from '@/utils/environment'
import { useAuthStore } from '@/store/authStore'
import {
  activeSchoolId,
  listenEffectiveSecurity,
  writeSecurityFields
} from '@/composables/listenEffectiveSecurity'

const DEFAULT_MINUTES = 3
const DEFAULT_MODE = 'table'
const VALID_MODES = ['names', 'table', 'full']

function normalizeMode(mode) {
  return VALID_MODES.includes(mode) ? mode : DEFAULT_MODE
}

class PrivacyBlurManager {
  constructor() {
    this.timeoutId = null
    this.isEnabled = ref(false)
    this.blurMinutes = ref(DEFAULT_MINUTES)
    this.blurMode = ref(DEFAULT_MODE)
    this.isBlurred = ref(false)
    // Per-user opt-out (localStorage, keyed by uid). District admin governs the
    // baseline; individuals may opt out of the blur without disabling it for all.
    this.userOptOut = ref(false)
    // Effective state = district enabled AND the user has not opted out.
    this.effectiveEnabled = computed(() => this.isEnabled.value && !this.userOptOut.value)
    this.lastActivity = Date.now()
    this.unsubscribeSettings = null

    this.activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click', 'keydown']

    this.init()
  }

  init() {
    devLog('🔒 Privacy blur system initialization started')
    this.setupActivityListeners()
    this.setupPrintListeners()
    this.setupAuthWatcher()
  }

  setupAuthWatcher() {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        devLog('🔒 User logged in, starting privacy blur system')
        this.loadUserOptOut()
        this.loadSettings()
        setTimeout(() => this.resetTimer(), 2000)
      } else {
        devLog('🔒 User logged out, clearing privacy blur')
        if (this.unsubscribeSettings) {
          this.unsubscribeSettings()
          this.unsubscribeSettings = null
        }
        this.userOptOut.value = false
        this.clearTimer()
        this.setBlurred(false)
      }
    })
  }

  loadSettings() {
    try {
      if (this.unsubscribeSettings) {
        this.unsubscribeSettings()
        this.unsubscribeSettings = null
      }

      this.unsubscribeSettings = listenEffectiveSecurity({
        onEffective: (data) => {
          if (data && (data.privacyBlurEnabled !== undefined || data.privacyBlurMinutes)) {
            this.isEnabled.value = data.privacyBlurEnabled === true
            this.blurMinutes.value = data.privacyBlurMinutes || DEFAULT_MINUTES
            this.blurMode.value = normalizeMode(data.privacyBlurMode)
          } else {
            this.applyDefaultsLocally()
          }
          devLog(
            `🔒 Privacy blur settings updated: enabled=${this.isEnabled.value}, minutes=${this.blurMinutes.value}, mode=${this.blurMode.value}`
          )
          if (auth.currentUser) this.resetTimer()
        },
        onError: () => this.applyDefaultsLocally()
      })
    } catch (error) {
      console.error('Failed to setup privacy blur settings listener:', error)
      this.applyDefaultsLocally()
    }
  }

  applyDefaultsLocally() {
    this.isEnabled.value = false
    this.blurMinutes.value = DEFAULT_MINUTES
    this.blurMode.value = DEFAULT_MODE
  }

  optOutKey() {
    return `privacyBlurOptOut:${auth.currentUser?.uid || 'anon'}`
  }

  loadUserOptOut() {
    try {
      this.userOptOut.value = localStorage.getItem(this.optOutKey()) === 'true'
    } catch {
      this.userOptOut.value = false
    }
    devLog(`🔒 Privacy blur user opt-out: ${this.userOptOut.value}`)
  }

  setUserOptOut(optOut) {
    this.userOptOut.value = !!optOut
    try {
      localStorage.setItem(this.optOutKey(), optOut ? 'true' : 'false')
    } catch {
      /* storage unavailable — preference simply won't persist */
    }
    if (optOut) this.setBlurred(false)
    this.resetTimer()
  }

  async updateSettings(enabled, minutes, mode) {
    try {
      await writeSecurityFields(useAuthStore().currentUser, activeSchoolId(), {
        privacyBlurEnabled: enabled,
        privacyBlurMinutes: minutes,
        privacyBlurMode: normalizeMode(mode)
      })

      this.isEnabled.value = enabled
      this.blurMinutes.value = minutes
      this.blurMode.value = normalizeMode(mode)

      devLog(
        `✅ Privacy blur settings updated: enabled=${enabled}, minutes=${minutes}, mode=${mode}`
      )

      if (auth.currentUser) {
        this.resetTimer()
      }
    } catch (error) {
      console.error('Failed to update privacy blur settings:', error)
      throw error
    }
  }

  setupActivityListeners() {
    this.boundActivityHandler = this.handleActivity.bind(this)
    this.activityEvents.forEach((event) => {
      document.addEventListener(event, this.boundActivityHandler, true)
    })
  }

  // File > Print / Ctrl+P can fire without a click. Wake so names aren't
  // printed through filter: blur() if the idle timer already fired.
  setupPrintListeners() {
    this.boundBeforePrint = () => this.wake()
    window.addEventListener('beforeprint', this.boundBeforePrint)
  }

  handleActivity() {
    if (!auth.currentUser) return

    const now = Date.now()
    this.lastActivity = now

    if (this.isBlurred.value) {
      devLog('🔒 Activity detected — removing privacy blur')
      this.setBlurred(false)
    }

    if (!this.effectiveEnabled.value) return

    this.resetTimer()
  }

  resetTimer() {
    this.clearTimer()

    if (!this.effectiveEnabled.value || !auth.currentUser) {
      this.setBlurred(false)
      return
    }

    const timeoutMs = Math.round(this.blurMinutes.value * 60 * 1000)
    devLog(`🔒 Privacy blur timer reset: ${this.blurMinutes.value} minutes`)

    this.timeoutId = setTimeout(() => {
      devLog('🔒 Privacy blur triggered after inactivity')
      this.setBlurred(true)
    }, timeoutMs)
  }

  setBlurred(blurred) {
    this.isBlurred.value = blurred
    const body = document.body

    body.classList.remove(
      'privacy-blur-active',
      'privacy-blur-names',
      'privacy-blur-table',
      'privacy-blur-full'
    )

    if (blurred) {
      body.classList.add('privacy-blur-active')
      body.classList.add(`privacy-blur-${normalizeMode(this.blurMode.value)}`)
      document.addEventListener('mousemove', this.boundActivityHandler, true)
    } else {
      document.removeEventListener('mousemove', this.boundActivityHandler, true)
    }
  }

  wake() {
    if (!auth.currentUser) return
    this.handleActivity()
  }

  clearTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  destroy() {
    this.clearTimer()
    document.removeEventListener('mousemove', this.boundActivityHandler, true)
    this.setBlurred(false)

    if (this.boundActivityHandler) {
      this.activityEvents.forEach((event) => {
        document.removeEventListener(event, this.boundActivityHandler, true)
      })
    }

    if (this.boundBeforePrint) {
      window.removeEventListener('beforeprint', this.boundBeforePrint)
      this.boundBeforePrint = null
    }

    if (this.unsubscribeSettings) {
      this.unsubscribeSettings()
      this.unsubscribeSettings = null
    }
  }
}

const privacyBlurManager = new PrivacyBlurManager()

export function usePrivacyBlur() {
  return {
    isEnabled: privacyBlurManager.isEnabled,
    blurMinutes: privacyBlurManager.blurMinutes,
    blurMode: privacyBlurManager.blurMode,
    isBlurred: privacyBlurManager.isBlurred,
    userOptOut: privacyBlurManager.userOptOut,
    effectiveEnabled: privacyBlurManager.effectiveEnabled,
    updateSettings: privacyBlurManager.updateSettings.bind(privacyBlurManager),
    setUserOptOut: privacyBlurManager.setUserOptOut.bind(privacyBlurManager),
    resetTimer: privacyBlurManager.resetTimer.bind(privacyBlurManager),
    wake: privacyBlurManager.wake.bind(privacyBlurManager)
  }
}
